import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DealManagementGuide = () => {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard/help")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Help Center
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Deal Management Guide</h1>
          </div>
          <p className="text-muted-foreground">
            Complete guide to the NIL deal lifecycle from submission to completion
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Deal Lifecycle Overview</CardTitle>
            <CardDescription>
              Understanding the complete journey of an NIL deal from initial submission through final approval and beyond
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">1</div>
                <div>
                  <h4 className="font-semibold">Submission</h4>
                  <p className="text-muted-foreground">Athlete submits deal with required information and documents</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">2</div>
                <div>
                  <h4 className="font-semibold">Automated Screening</h4>
                  <p className="text-muted-foreground">System checks for conflicts, prohibited categories, and missing information</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">3</div>
                <div>
                  <h4 className="font-semibold">Compliance Review</h4>
                  <p className="text-muted-foreground">Compliance officer manually reviews deal details and documentation</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">4</div>
                <div>
                  <h4 className="font-semibold">Decision</h4>
                  <p className="text-muted-foreground">Deal is approved, rejected, or returned for more information</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">5</div>
                <div>
                  <h4 className="font-semibold">Active Monitoring</h4>
                  <p className="text-muted-foreground">Approved deals are tracked through completion with ongoing compliance</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">6</div>
                <div>
                  <h4 className="font-semibold">Completion</h4>
                  <p className="text-muted-foreground">Deal ends, final documentation archived, data used for reporting</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deal Status Definitions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-medium">Draft</span>
              </div>
              <p className="text-sm text-muted-foreground">Deal has been started but not yet submitted for review</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm font-medium">Pending</span>
              </div>
              <p className="text-sm text-muted-foreground">Deal submitted and awaiting compliance review</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">Under Review</span>
              </div>
              <p className="text-sm text-muted-foreground">Compliance officer is actively reviewing the deal</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">Approved</span>
              </div>
              <p className="text-sm text-muted-foreground">Deal meets all requirements and athlete may proceed</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">Rejected</span>
              </div>
              <p className="text-sm text-muted-foreground">Deal violates policies and cannot be pursued</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">Completed</span>
              </div>
              <p className="text-sm text-muted-foreground">Deal term has ended and all obligations fulfilled</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Common Deal Scenarios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Social Media Endorsement</h4>
              <p className="text-sm text-muted-foreground">
                Athlete posts branded content on Instagram/TikTok. Typically $500-$5,000 per post.
                Required: Screenshots of proposed content, posting schedule, payment terms.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Local Business Appearance</h4>
              <p className="text-sm text-muted-foreground">
                Athlete makes in-person appearance at restaurant, car dealership, etc. Usually $1,000-$3,000 per event.
                Required: Event details, time commitment, usage rights for photos.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Equipment Sponsorship</h4>
              <p className="text-sm text-muted-foreground">
                Athlete receives free gear in exchange for social media posts. Value based on retail price of equipment.
                Required: List of items, retail values, posting obligations.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Camp or Clinic Instruction</h4>
              <p className="text-sm text-muted-foreground">
                Athlete coaches at youth camp. $2,000-$10,000 depending on role and duration.
                Required: Camp dates, time commitment, insurance coverage verification.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DealManagementGuide;